import React, { useCallback, useImperativeHandle, forwardRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import classnames from 'classnames';
import { CarouselArrow } from './CarouselArrow';

type CarouselProps = {
  children: React.ReactNode;
  className?: string;
  emblaOptions?: Partial<EmblaOptionsType>;
  showArrows?: boolean;
  fullWidthSlides?: boolean;
  slidesPerView?: 1 | 2 | 3 | 4 | 5 | 6;
  gap?: number;
  customPrevArrow?: React.ReactNode;
  customNextArrow?: React.ReactNode;
  onSelectedIndexChange?: (index: number) => void;
};

export type CarouselImperativeHandle = {
  scrollPrev: () => void;
  scrollNext: () => void;
  emblaApi: any;
};

const Carousel = forwardRef<CarouselImperativeHandle, CarouselProps>(
  (
    {
      children,
      className,
      emblaOptions = {
        loop: true,
        duration: 20,
        align: 'start',
        slidesToScroll: 'auto',
        containScroll: 'trimSnaps',
        startIndex: 0,
      },
      showArrows = true,
      fullWidthSlides = false,
      slidesPerView,
      gap,
      customPrevArrow,
      customNextArrow,
      onSelectedIndexChange,
    },
    ref
  ) => {
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const canScroll = canScrollPrev || canScrollNext;

    // Dynamically adjust embla options based on scroll state
    // this is to force empbla update when arrows are hidden
    const dynamicEmblaOptions = React.useMemo(
      () => ({
        ...emblaOptions,
        duration: canScroll ? emblaOptions.duration : 0,
        active: canScroll ? emblaOptions.active ?? true : false,
      }),
      [emblaOptions, canScroll]
    );

    const [emblaRef, emblaApi] = useEmblaCarousel(dynamicEmblaOptions);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    // Ref keeps the embla listener below from re-subscribing on every render
    const onSelectedIndexChangeRef = React.useRef(onSelectedIndexChange);
    onSelectedIndexChangeRef.current = onSelectedIndexChange;
    const childrenArray = React.Children.toArray(children);
    const hasMultipleItems = childrenArray.length > 1;

    const handleResize = useCallback(() => {
      if (emblaApi) {
        // Debounce the reinitialization to prevent too frequent updates
        const timeoutId = setTimeout(() => {
          emblaApi.reInit();
          // Ensure we maintain the current scroll position
          const currentIndex = emblaApi.selectedScrollSnap();
          emblaApi.scrollTo(currentIndex);
        }, 150);

        return () => clearTimeout(timeoutId);
      }
    }, [emblaApi]);

    // Reinitialize carousel when layout props change
    React.useEffect(() => {
      if (emblaApi) {
        emblaApi.reInit();
      }
    }, [emblaApi, fullWidthSlides, slidesPerView, childrenArray.length]);

    React.useEffect(() => {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [handleResize]);

    React.useEffect(() => {
      if (emblaApi) {
        const updateScrollState = () => {
          // Check if all slides fit in view
          const totalSlides = childrenArray.length;
          const allSlidesFit = slidesPerView && totalSlides <= slidesPerView;

          // If all slides fit, hide arrows regardless of loop mode
          if (allSlidesFit) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
          } else {
            setCanScrollPrev(emblaApi.canScrollPrev());
            setCanScrollNext(emblaApi.canScrollNext());
          }
        };
        const onSelect = () => {
          const index = emblaApi.selectedScrollSnap();
          setSelectedIndex(index);
          onSelectedIndexChangeRef.current?.(index);
        };

        updateScrollState();
        emblaApi.on('select', updateScrollState);
        emblaApi.on('reInit', updateScrollState);
        emblaApi.on('select', onSelect);
        onSelect();

        return () => {
          emblaApi.off('select', updateScrollState);
          emblaApi.off('reInit', updateScrollState);
          emblaApi.off('select', onSelect);
        };
      }
    }, [emblaApi, childrenArray.length, slidesPerView]);

    const scrollPrev = () => emblaApi?.scrollPrev();
    const scrollNext = () => emblaApi?.scrollNext();

    const renderPrevArrow = () => {
      if (customPrevArrow) {
        return React.cloneElement(customPrevArrow as React.ReactElement<any>, {
          onClick: scrollPrev,
          isVisible: canScrollPrev,
        });
      }
      return showArrows ? (
        <CarouselArrow
          onClick={scrollPrev}
          isVisible={canScrollPrev}
          direction="prev"
        />
      ) : null;
    };

    const renderNextArrow = () => {
      if (customNextArrow) {
        return React.cloneElement(customNextArrow as React.ReactElement<any>, {
          onClick: scrollNext,
          isVisible: canScrollNext,
        });
      }
      return showArrows ? (
        <CarouselArrow
          onClick={scrollNext}
          isVisible={canScrollNext}
          direction="next"
        />
      ) : null;
    };

    const gapPx = gap || 0;
    const isLoopMode = emblaOptions?.loop;

    // Calculate slide width including gap
    const slideWidth = slidesPerView
      ? `calc((100% - ${(slidesPerView - 1) * gapPx}px) / ${slidesPerView})`
      : undefined;

    const containerStyle = slidesPerView
      ? {
          ['--slide-size' as any]: slideWidth,
        }
      : fullWidthSlides
      ? {
          ['--slide-size' as any]: '100%',
          ['--slide-gap' as any]: `${gapPx}px`,
        }
      : {
          gap: gap ? `${gap}px` : undefined,
        };

    useImperativeHandle(ref, () => ({
      scrollPrev,
      scrollNext,
      emblaApi,
    }));

    return (
      <div className={classnames('relative w-full h-full', className)}>
        {hasMultipleItems || slidesPerView || fullWidthSlides ? (
          <div
            className="overflow-hidden w-full h-full embla__viewport"
            ref={emblaRef}
            role="region"
            aria-label="Image carousel"
            aria-live="polite"
          >
            <div
              className={classnames(
                'flex h-full w-full',
                {
                  embla__container: !!slidesPerView || fullWidthSlides,
                },
                'w-full'
              )}
              style={containerStyle}
            >
              {childrenArray.map((child, index) => {
                const isLast = index === childrenArray.length - 1;
                const addGap = gap && (!isLast || (isLoopMode && isLast));
                if (slidesPerView || fullWidthSlides) {
                  return (
                    <div
                      key={index}
                      className={classnames(
                        'embla__slide min-w-0 relative shrink',
                        { 'w-full': fullWidthSlides }
                      )}
                      style={{
                        zIndex: index === selectedIndex ? 2 : 1,
                        marginRight: addGap ? `${gap}px` : undefined,
                      }}
                      role="group"
                      aria-label={`Slide ${index + 1} of ${
                        childrenArray.length
                      }`}
                      aria-hidden={index !== selectedIndex}
                    >
                      {child}
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className="inline-block align-top min-w-0 relative shrink"
                      style={{
                        zIndex: index === selectedIndex ? 2 : 1,
                        marginRight: addGap ? `${gap}px` : undefined,
                      }}
                      role="group"
                      aria-label={`Slide ${index + 1} of ${
                        childrenArray.length
                      }`}
                      aria-hidden={index !== selectedIndex}
                    >
                      {child}
                    </div>
                  );
                }
              })}
            </div>
            {hasMultipleItems && (
              <>
                {renderPrevArrow()}
                {renderNextArrow()}
              </>
            )}
          </div>
        ) : (
          <div className="flex justify-center items-center w-full h-full">
            {children}
          </div>
        )}
      </div>
    );
  }
);

Carousel.displayName = 'Carousel';

export { CarouselArrow };
export default Carousel;
